import classnames from 'classnames';
import React, { useEffect, useState } from 'react';
import { Flipped, Flipper } from 'react-flip-toolkit';

import type { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { Button } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { CheckmarkAnimation } from '@eika/checkmark-animation';
import { Loader } from '@eika/loader';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import ErrorCard from '../../../../components/errorcard/ErrorCard';
import CenteredModalContent from '../../../../components/shared/CenteredModalContent/CenteredModalContent';
import { StickyFooterRow } from '../../../../components/shared/StickyFooter';
import { useConfig } from '../../../../defaultConfig';
import { accountListSelector, useAccounts, useUpdateOrder } from '../../../../fetchHooks/useAccount';
import { useCreditcards, useRefreshCreditCards } from '../../../../fetchHooks/useCards';
import useTranslation from '../../../../hooks/useTranslation';
import { AccountCardItem } from '../../../../models/account';
import { EditAccountBox, SettingsAccountOrderNeedMoreAccounts } from '../DragAndDropAccountsList/components';
import mapAllAccountCardItems from '../DragAndDropAccountsList/mapAccountCardItems';
import mapAccountProperties from './mapAccountProperties';

import './EditAccountList.scss';

interface Props {
    onCompleted: () => void;
}

const EditAccountListBody: React.FC<Props> = ({ onCompleted }) => {
    const { featureToggles } = useConfig();
    const translated = useTranslation();
    const { data: accounts, ...accountState } = useAccounts({ select: accountListSelector });
    const accountNumbers = accounts?.map((account) => account.accountNumber.value) ?? [];
    const { data: cards, ...cardsState } = useCreditcards(accountNumbers, featureToggles.enableCreditCardsFrontpage);
    const [checkedItems, setCheckedItems] = useState(new Map());
    const [accountsInOrder, setAccountsInOrder] = useState<AccountCardItem[]>();
    const [focusedAccount, setFocusedAccount] = useState<AccountCardItem>();
    const [invalidAccount, setInvalidAccount] = useState<AccountCardItem>();
    const [showSaveButton, setShowSaveButton] = useState(false);
    const [isInitialRender, setIsInitialRender] = useState(true);

    const updateAccountOrder = useUpdateOrder();
    const refreshCreditCards = useRefreshCreditCards();
    const accountCardItems = mapAllAccountCardItems(accounts, cards);

    useEffect(() => {
        if (
            isInitialRender &&
            (cardsState.isFetched || !featureToggles.enableCreditCardsFrontpage) &&
            accountState.isFetched
        ) {
            const map = new Map();
            accountCardItems?.forEach((account) => map.set(account.accountNumber.value, account.favorite));
            setCheckedItems(map);
            setAccountsInOrder(accountCardItems);
            setIsInitialRender(false);
        }
    }, [
        accountCardItems,
        accountState.isFetched,
        cards,
        cardsState.isFetched,
        featureToggles.enableCreditCardsFrontpage,
        isInitialRender,
    ]);

    const handleChange = (account: AccountCardItem, checked: boolean) => {
        setInvalidAccount(undefined);
        const numberOfFavorites = Array.from(checkedItems.values()).filter((value) => !!value).length;
        if (numberOfFavorites < 2 && !checked) {
            setInvalidAccount(account);
            return;
        }
        setCheckedItems(new Map(checkedItems.set(account.accountNumber.value, checked)));
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const changedAccounts = mapAccountProperties(accountsInOrder!, checkedItems);
        if (!changedAccounts) {
            onCompleted();
            return;
        }
        updateAccountOrder.mutateAsync(changedAccounts).then(() => {
            refreshCreditCards();
            onCompleted();
        });
    };

    const moveElement = (list: unknown[], index: number, direction: -1 | 1) => {
        const newAccounts = [...list];
        const temp = newAccounts[index];
        newAccounts[index] = newAccounts[index + direction];
        newAccounts[index + direction] = temp;
        return newAccounts;
    };

    const moveUp = (account: AccountCardItem): boolean => {
        setInvalidAccount(undefined);
        if (!accountsInOrder) return false;

        if (accountsInOrder[0] === account) {
            return true;
        }
        setFocusedAccount(account);
        const index = accountsInOrder.findIndex((element) => element === account);
        setAccountsInOrder((items) => moveElement(items!, index, -1) as AccountCardItem[]);
        return false;
    };

    const moveDown = (account: AccountCardItem): boolean => {
        setInvalidAccount(undefined);
        if (!accountsInOrder) return false;

        if (accountsInOrder[accountsInOrder.length - 1] === account) {
            return true;
        }
        setFocusedAccount(account);
        const index = accountsInOrder.findIndex((element) => element === account);
        setAccountsInOrder((items) => moveElement(items!, index, 1) as AccountCardItem[]);
        return false;
    };

    useEffect(() => {
        const hasChange = accountsInOrder?.some((account, index) => {
            if (accountCardItems && account.accountNumber.value !== accountCardItems[index].accountNumber.value) {
                return true;
            }
            return checkedItems.get(account.accountNumber.value) !== account.favorite;
        });
        setShowSaveButton(hasChange ?? false);
    }, [accountCardItems, accountsInOrder, checkedItems]);

    if (updateAccountOrder.error) {
        return (
            <ErrorCard
                exception={updateAccountOrder.error as RequestExceptionWithResponse}
                title={translated.errors.somethingWentWrong}
                description={translated.editAccounts.errorNotSaved}
                icon={<RichIcon type={RichIconTypes.DATAFEIL_1} ariaLabel={translated.editAccounts.technicalError} />}
                isModal
            >
                <Button onClick={onCompleted}>{translated.ok}</Button>
            </ErrorCard>
        );
    }

    if (accountState.isLoading || cardsState.isLoading) {
        return (
            <div className="action-result-centered">
                <Loader />
            </div>
        );
    }

    if (updateAccountOrder.isSuccess) {
        return (
            <CenteredModalContent withMessage>
                <CheckmarkAnimation />
                <h3>{translated.editAccounts.success}</h3>
            </CenteredModalContent>
        );
    }

    if (accountState.error) {
        return (
            <ErrorCard
                exception={accountState.error}
                title={translated.errors.somethingWentWrong}
                description={translated.editAccounts.accountsError}
                icon={<RichIcon type={RichIconTypes.DATAFEIL_1} ariaLabel={translated.editAccounts.technicalError} />}
                isModal
            >
                <Button onClick={() => accountState.refetch()}>{translated.tryAgain}</Button>
            </ErrorCard>
        );
    }

    if (accountCardItems.length < 2) {
        return <SettingsAccountOrderNeedMoreAccounts okButton={onCompleted} />;
    }

    return (
        <div className={classnames('edit-account-list')}>
            <p className="edit-account-list__info">{translated.editAccounts.info}</p>
            <form onSubmit={onSubmit}>
                <Flipper flipKey={JSON.stringify(accountsInOrder)} onComplete={() => setFocusedAccount(undefined)}>
                    {accountsInOrder?.map((account) => (
                        <Flipped
                            key={account.accountNumber.value}
                            flipId={`edit-account-box-${account.accountNumber.value}`}
                            spring={focusedAccount?.accountNumber === account.accountNumber ? 'stiff' : 'veryGentle'}
                        >
                            <EditAccountBox
                                account={account}
                                checked={checkedItems.get(account.accountNumber.value)}
                                onChange={handleChange}
                                moveUp={moveUp}
                                moveDown={moveDown}
                                focused={focusedAccount === account}
                                invalid={invalidAccount === account}
                            />
                        </Flipped>
                    ))}
                </Flipper>
                {showSaveButton && (
                    <StickyFooterRow>
                        <ButtonWithSpinner
                            size="small"
                            buttonType="positive"
                            type="submit"
                            showSpinner={updateAccountOrder.isPending}
                        >
                            {translated.save}
                        </ButtonWithSpinner>
                    </StickyFooterRow>
                )}
            </form>
        </div>
    );
};

export default EditAccountListBody;
